import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bell, Mail, MessageCircle, Megaphone, HeartPulse, Check, Trash2, Inbox } from "lucide-react";

const ICON_MAP = {
  mail: Mail, message: MessageCircle, megaphone: Megaphone, "heart-pulse": HeartPulse, bell: Bell,
};

const fmtRel = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
};

export default function NotificationBell({ onOpenInbox }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const poll = async () => {
    try {
      const r = await api.get("/inbox/poll");
      setUnread(r.data.notifications_unread || 0);
    } catch { /* noop */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inbox/notifications", { params: { limit: 30 } });
      setItems(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await load();
  };

  const markRead = async (id) => {
    try { await api.post(`/inbox/notifications/${id}/read`); } catch { /* noop */ }
    setItems((xs) => xs.map((x) => x.id === id ? { ...x, read_at: new Date().toISOString() } : x));
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAll = async () => {
    try { await api.post("/inbox/notifications/mark-all-read"); } catch { /* noop */ }
    setItems((xs) => xs.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    setUnread(0);
  };

  const remove = async (id) => {
    try { await api.delete(`/inbox/notifications/${id}`); } catch { /* noop */ }
    setItems((xs) => xs.filter((x) => x.id !== id));
  };

  const onClick = (n) => {
    if (!n.read_at) markRead(n.id);
    if (n.action_url?.startsWith("/inbox/")) {
      setOpen(false);
      onOpenInbox?.(n.action_url.replace("/inbox/", ""));
    } else if (n.action_url) {
      window.location.assign(n.action_url);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="relative bg-white/15 hover:bg-white/25 text-white rounded-md h-9 px-2 sm:px-3 flex items-center transition"
        data-testid="notification-bell"
        title="Notifications"
      >
        <Bell className="h-4 w-4 icon-wiggle" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#f37021] text-[10px] font-bold flex items-center justify-center ring-2 ring-[#004a99]"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] max-h-[70vh] bg-white text-slate-900 rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50 flex flex-col"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ background: "linear-gradient(135deg,#004a99,#0066cc)" }}>
            <div className="flex items-center gap-2 text-white">
              <Inbox className="h-4 w-4" />
              <span className="font-heading font-bold text-sm">Notifications</span>
              {unread > 0 && <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">{unread} new</span>}
            </div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-white/90 hover:text-white underline" data-testid="notif-mark-all-read">
                Mark all read
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && <div className="p-6 text-center text-sm text-slate-400">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="notif-empty">
                <Bell className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                You're all caught up.
              </div>
            )}
            {items.map((n) => {
              const Icon = ICON_MAP[n.icon] || Bell;
              const isUnread = !n.read_at;
              return (
                <div
                  key={n.id}
                  className={`group px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition ${isUnread ? "bg-[#fff7f0]" : ""}`}
                  onClick={() => onClick(n)}
                  data-testid="notification-item"
                  data-notif-id={n.id}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg flex-shrink-0 flex items-center justify-center"
                         style={{ background: isUnread ? "#f37021" : "#e2e8f0" }}>
                      <Icon className="h-4 w-4" style={{ color: isUnread ? "#fff" : "#475569" }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`text-sm leading-snug ${isUnread ? "font-bold text-slate-900" : "text-slate-700"}`}>
                          {n.title}
                        </div>
                        <span className="text-[10px] text-slate-400 flex-shrink-0 mt-0.5">{fmtRel(n.created_at)}</span>
                      </div>
                      {n.body_preview && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.body_preview}</div>}
                    </div>
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
                      {isUnread && (
                        <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                                className="text-slate-400 hover:text-emerald-600 p-1" title="Mark read">
                          <Check className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                              className="text-slate-400 hover:text-red-600 p-1" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
